import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SourceIcon } from '@/components/ui/source-icon'
import { useSharePointStore } from '@/stores/sharepoint-store'

export function IntegrationsTab() {
  const {
    connection,
    isConnected,
    isCheckingConnection,
    checkConnection,
    beginConnect,
    disconnect,
  } = useSharePointStore()

  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  useEffect(() => {
    void checkConnection()
  }, [checkConnection])

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-sm font-medium">Integrations</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          Connect BidContext to the tools your team already uses. Each connection
          is per-user — your teammates connect their own accounts.
        </p>
      </div>

      {/* SharePoint */}
      <div className="border border-[hsl(var(--border))] rounded-xl bg-[hsl(var(--card))] p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <SourceIcon source="sharepoint" size={28} />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium">SharePoint</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              Import documents from your company's SharePoint into the Library, or
              save AI-written responses straight into a SharePoint folder.
            </p>

            {isCheckingConnection && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Checking…</p>
            )}

            {!isCheckingConnection && isConnected && connection && (
              <div className="mt-3 space-y-2">
                <div className="text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-[hsl(var(--status-success))]" />
                    Connected as {connection.userPrincipalName}
                    {connection.displayName && (
                      <span className="text-[hsl(var(--muted-foreground))]">
                        ({connection.displayName})
                      </span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Connected on {formatDate(connection.connectedAt)}
                  {connection.lastUsedAt && ` · Last used ${formatDate(connection.lastUsedAt)}`}
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDisconnect(true)}
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            )}

            {!isCheckingConnection && !isConnected && (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={() => void beginConnect('/app/settings/integrations')}
                >
                  Connect SharePoint
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect SharePoint?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to reconnect before you can import or save again. Previously
              imported documents stay in your Library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void disconnect()}>
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return iso
  }
}
